<template>
  <div class="name-search-page">
    <h1 class="with-subhead">{{ capitalize(props.name) }}</h1>
    <p class="subhead">{{ partDisplayNameRef }}</p>
    <div class="btn-group scope-select" role="group" aria-label="Scope">
      <input type="radio" class="btn-check" name="scope" id="scope-ancestors" autocomplete="off" value="ancestors" v-model="scopeMode"/>
      <label class="btn btn-outline-primary btn-sm" for="scope-ancestors">My family</label>
      <input type="radio" class="btn-check" name="scope" id="scope-entire" autocomplete="off" value="entireTree" v-model="scopeMode"/>
      <label class="btn btn-outline-primary btn-sm" for="scope-entire">Entire tree</label>
    </div>
    <ScopeLoadProgress v-if="scopeLoading"/>
    <template v-else>
    <h3 v-if="personsRef.length">{{ personsRef.length }} people</h3>
    <div class="timeline-chart">
      <!-- Gridlines with year labels at top -->
      <div class="gridlines">
        <div v-for="year in gridYears" :key="year" class="gridline" :class="{ 'minor': year % 100 !== 0 }" :style="{ left: yearToPercent(year) + '%' }">
          <span class="year-label">{{ year }}</span>
        </div>
      </div>

      <!-- Person rows -->
      <ul class="list-unstyled person-list">
        <li v-for="vp in personsRef" :key="vp.id"
          class="person-row" :class="{ expanded: expandedId === vp.id, 'no-bar': !vp.birthYear }">
          <div class="person-bar" :style="getBarStyle(vp)"
            :role="vp.birthYear ? 'button' : undefined"
            :tabindex="vp.birthYear ? 0 : undefined"
            :aria-expanded="vp.birthYear ? expandedId === vp.id : undefined"
            :aria-label="vp.displayName"
            @click="vp.birthYear && toggleExpand(vp)"
            @keydown.enter.prevent="vp.birthYear && toggleExpand(vp)"
            @keydown.space.prevent="vp.birthYear && toggleExpand(vp)">
            <div class="person-info">
              <div class="person-name-line">
                <div v-if="getBirthPlaceFlag(vp)" class="fi" :class="'fi-' + getBirthPlaceFlag(vp)"></div>
                <span class="person-name">{{ vp.displayName }}</span>
                <router-link :to="vp.pageUrl!" class="person-link" aria-label="View person" @click.stop>
                  <ArrowRightIcon />
                </router-link>
              </div>
              <div v-if="getRelationPathText(vp)" class="relation-path">{{ getRelationPathText(vp) }}</div>
            </div>
            <template v-if="vp.birthYear">
              <LifespanBar
                :birth-year="vp.birthYear" :death-year="vp.deathYear"
                :age="getAgeAtDeath(vp)" :living="vp.displayProperties?.living"
                :layout="expandedId === vp.id ? 'full' : 'small'"
                class="lifespan-bar-instance"/>
              <div v-show="expandedId === vp.id" class="lifespan-icons">
                <div v-if="vp.deathYear" class="icon-label death-end">
                  <img :src="sunsetIcon" alt="death"/>
                </div>
                <div class="icon-label birth-end">
                  <img :src="sunriseIcon" alt="birth"/>
                </div>
              </div>
            </template>
          </div>
        </li>
      </ul>
    </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.scope-select {
  margin-top: 0.5rem;
}

.timeline-chart {
  position: relative;
  margin-top: 1rem;
  padding-top: 1.5rem; // room for year labels
}

.gridlines {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;

  .gridline {
    position: absolute;
    top: 0;
    bottom: 0;
    border-left: 1px solid #e0e0e0;

    .year-label {
      position: absolute;
      top: 0;
      left: 2px;
      font-size: 0.65rem;
      color: #aaa;
      white-space: nowrap;
    }

    &.minor {
      display: none;

      @media (min-width: 768px) {
        display: block;
      }
    }
  }
}

.person-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.person-row {
  position: relative;
  height: 2.75rem;
  transition: height 0.2s ease-in-out;
  overflow: hidden;

  &.no-bar {
    height: auto;
  }

  &.expanded {
    height: 6.5rem;
  }
}

.person-bar {
  position: absolute;
  top: 0;
  display: flex;
  flex-direction: column;

  .person-row.no-bar & {
    position: static;
  }

  .person-row:not(.no-bar) & {
    cursor: pointer;
  }
}

.person-info {
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  .person-row.no-bar & {
    align-items: flex-start;
  }
}

.person-name-line {
  display: flex;
  align-items: center;
  gap: 0.25rem;

  .fi {
    width: 1rem;
    height: 0.75rem;
    opacity: 0.6;
    flex-shrink: 0;
    background-size: contain;
  }
}

.person-name {
  font-size: 0.8rem;
  color: #333;
  white-space: nowrap;
}

.person-link {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  color: var(--bs-link-color);

  svg {
    width: 0.8rem;
    height: 0.8rem;
  }

  &:hover {
    color: var(--bs-link-hover-color);
  }
}

.relation-path {
  font-size: 0.65rem;
  color: #999;
  white-space: nowrap;
  margin-top: -2px;
}

.lifespan-bar-instance {
  margin-top: 0.25rem;
}

.lifespan-icons {
  position: relative;
  height: 0;

  .icon-label {
    position: absolute;
    top: 0.1rem;

    img {
      width: 1.2rem;
      opacity: 0.2;
    }

    &.death-end {
      left: 0;
      transform: translateX(-50%);
    }

    &.birth-end {
      right: 0;
      transform: translateX(50%);
    }
  }
}
</style>

<script lang="ts" setup>
import { computed, Ref, ref, watch } from 'vue'
import { ArrowRightIcon } from '@zhuowenli/vue-feather-icons'
import LifespanBar from '../time/LifespanBar.vue'
import { usePlaceStore } from '@/rd/PlaceStore'
import { useViewPersonStore } from '@/rd/ViewPersonStore'
import { useExploreStore } from '../ExploreStore'
import { useViewScopeStore } from '@/explore/ViewScopeStore'
import { LoadMode } from '@/util/AsyncData'
import ScopeLoadProgress from '@/explore/ScopeLoadProgress.vue'
import { getPersonNamePartDisplayText, PersonName, PersonNamePart, ViewPerson } from '@/rd/ResearchDataModel'
import { capitalize } from '@/util/LanguageUtil'
import { DurationUtil } from '@/util/LuxonUtil'
import { useRelationTextBuilder } from '@/rd/RelationPath'
import { DateTime } from 'luxon'

const props = defineProps<{
  part: PersonNamePart
  name: string
}>()

const defaultMinYear = 1700
const maxYear = DateTime.now().year
const unknownLifespan = 80

const viewPersonStore = useViewPersonStore()
const placeStore = usePlaceStore()
const exploreStore = useExploreStore()
const viewScopeStore = useViewScopeStore()

type ScopeMode = 'ancestors' | 'entireTree'
const scopeMode = ref<ScopeMode>('ancestors')

const scopeInfoRef = computed(() =>
  viewScopeStore.getAsyncScopeInfo(exploreStore.startPersonId, LoadMode.EnsureLoaded)?.data)

const scopeLoading = computed(() =>
  scopeMode.value === 'ancestors' &&
  !viewScopeStore.getAsyncScopeInfo(exploreStore.startPersonId, LoadMode.EnsureLoaded)?.isLoaded)
const { getRelationText } = useRelationTextBuilder()

const sunriseIcon = new URL('@/assets/icons/sunrise_gray_60.png', import.meta.url).href
const sunsetIcon = new URL('@/assets/icons/sunset_gray_60.png', import.meta.url).href
const expandedId = ref<string | null>(null)

const personsRef = ref([]) as Ref<ViewPerson[]>
const partDisplayNameRef = computed(() => capitalize(getPersonNamePartDisplayText(props.part)))
const minYearRef = computed(() =>
  Math.floor(personsRef.value.reduce((min, vp) => Math.min(min, vp.birthYear ?? min), defaultMinYear) / 100) * 100)

const gridYears = computed(() => {
  const start = Math.ceil(minYearRef.value / 50) * 50
  const years: number[] = []
  for (let y = start; y <= maxYear; y += 50) {
    years.push(y)
  }
  return years
})

watch([props, scopeMode, scopeInfoRef], async () => {
  const nameKey = props.name.toLowerCase()

  let viewPersons: ViewPerson[]

  if (scopeMode.value === 'ancestors') {
    if (!scopeInfoRef.value) {
      personsRef.value = []
      return
    }
    viewPersons = []
    for (const id of scopeInfoRef.value.personIds) {
      const vp = viewPersonStore.getLoadedPerson(id)
      if (vp) viewPersons.push(vp)
    }
  } else {
    viewPersons = (await viewPersonStore.searchByNamesAsync([nameKey]))
      .map(a => a.data!)
  }

  personsRef.value = viewPersons
    .filter(p => new PersonName(p.displayProperties.name).getPart(props.part)?.toLowerCase().includes(nameKey))
    .sort((a, b) => {
      const ay = a.birthYear, by = b.birthYear
      if (ay == null && by == null) return 0
      if (ay == null) return -1
      if (by == null) return 1
      return by - ay // desc
    })
}, { immediate: true })

watch([personsRef, () => exploreStore.startPersonId], async () => {
  if (exploreStore.startPersonId) {
    await viewPersonStore.ensureRelationPathsLoadedAsync(exploreStore.startPersonId, personsRef.value.map(p => p.id!))
  }
}, { immediate: true })

function toggleExpand(vp: ViewPerson) {
  expandedId.value = expandedId.value === vp.id ? null : vp.id!
}

function getAgeAtDeath(vp: ViewPerson) {
  const dp = vp.displayProperties
  const birth = dp.birthSortDate.date1
  const death = dp.deathSortDate.date1
  if (birth && !birth.isEmpty && death && !death.isEmpty) {
    return DurationUtil.toAge(death.diff(birth), true)
  }
}

function getBirthPlaceFlag(vp: ViewPerson) {
  return getBirthPlace(vp)?.countryCode?.toLowerCase()
}

function getBirthPlace(vp: ViewPerson) {
  if (vp.displayProperties.birthPlaceId) {
    return placeStore.asyncPlaces.get(vp.displayProperties.birthPlaceId)?.data
  }
  return undefined
}

function getDisplayBirthYear(vp: ViewPerson) {
  return vp.birthYear ?? (vp.deathYear ? vp.deathYear - unknownLifespan : undefined)
}

function getDisplayDeathYear(vp: ViewPerson) {
  return vp.deathYear ?? (vp.birthYear ? vp.birthYear + unknownLifespan : undefined)
}

function yearToPercent(year: number) {
  // Present (maxYear) at 0% (left), past (minYear) at 100% (right)
  return ((maxYear - year) / (maxYear - minYearRef.value)) * 100
}

function getBarStyle(vp: ViewPerson) {
  const birthYear = getDisplayBirthYear(vp)
  const deathYear = getDisplayDeathYear(vp)
  if (birthYear != null && deathYear != null) {
    const left = yearToPercent(deathYear)
    const width = Math.max(yearToPercent(birthYear) - left, 1)
    return { left: left + '%', width: width + '%' }
  }
}

function getRelationPathText(vp: ViewPerson) {
  if (exploreStore.startPersonId) {
    const path = viewPersonStore.getLoadedRelationPath(exploreStore.startPersonId, vp.id!)
    if (path) {
      return getRelationText(path)
    }
  }
}
</script>
